import { cva, type VariantProps } from 'class-variance-authority'
import { Input as AriaInput, type InputProps as AriaInputProps } from 'react-aria-components'
import { cn } from '../../lib/cn'
import { focusRing } from '../../lib/focus-ring'

/**
 * The text field on the Creative canvas (204:3240): Property 1 = Default / Hover /
 * Completed. Hover is the pointer state (react-aria's `data-hovered`), not a prop;
 * Completed is the filled look of a field whose value is settled.
 *
 * Figma strokes sit inside the frame, so the 1px border is an inset ring and the
 * text starts 16px in on every variant, stroked or not.
 *
 * Keyboard focus takes the kit's accent outline, which is not drawn in the Figma.
 */
export const inputVariants = cva(
	['h-10 w-full min-w-0 rounded-atlas-md px-4 font-atlas-sans text-atlas-body text-atlas-dark', focusRing],
	{
		variants: {
			status: {
				default: 'bg-atlas-light inset-ring inset-ring-atlas-stroke data-hovered:inset-ring-atlas-light-dark/30',
				completed: 'bg-atlas-background',
			},
		},
		defaultVariants: { status: 'default' },
	},
)

export interface InputProps extends Omit<AriaInputProps, 'className'>, VariantProps<typeof inputVariants> {
	className?: string
}

/**
 * A single-line text input, built on react-aria's Input so it picks up a
 * surrounding `TextField`'s label, description and validation wiring.
 */
export function Input({ status, className, ...props }: InputProps) {
	return <AriaInput {...props} className={cn(inputVariants({ status }), className)} />
}
